import { Link, useParams } from "react-router-dom";
import { Landmark, ScrollText, Sun, Siren, ArrowRight, Phone } from "lucide-react";
import Layout from "@/components/Layout";
import { Loading, PageTitle } from "@/components/Bits";
import { useT } from "@/lib/i18n";
import { useApi } from "@/lib/data";

export const PAGE_ICONS = { landmark: Landmark, scroll: ScrollText, sun: Sun, siren: Siren };
const PHONE = /(\+?\d[\d-]{1,14}\d)/g;

const HELPLINES = ["100", "101", "102", "108", "112", "181", "1091", "1098"];
const isPhone = (part) => { const d = part.replace(/\D/g, ""); return d.length >= 8 || HELPLINES.includes(d); };

const Phoned = ({ text }) => text.split(PHONE).map((part, i) => (
  i % 2 && isPhone(part)
    ? <a key={i} href={`tel:${part.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-800" data-testid="page-phone-link"><Phone className="h-3 w-3" />{part}</a>
    : <span key={i}>{part}</span>
));

const block = (lines, key) => {
  if (lines.every((l) => l.startsWith("- "))) {
    return <ul key={key} className="mt-3 space-y-2">{lines.map((l, j) => <li key={j} className="flex gap-2 text-slate-700"><span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-saffron" /><span><Phoned text={l.slice(2)} /></span></li>)}</ul>;
  }
  return <p key={key} className="mt-3 leading-relaxed text-slate-700"><Phoned text={lines.join(" ")} /></p>;
};

const Body = ({ text }) => text.split(/\n\s*\n/).flatMap((chunk, i) => {
  const lines = chunk.split("\n").filter(Boolean);
  if (!lines.length) return [];
  if (!lines[0].startsWith("## ")) return [block(lines, i)];
  const out = [<h2 key={`h${i}`} className="mt-8 font-display text-lg font-extrabold text-ink md:text-xl">{lines[0].slice(3)}</h2>];
  if (lines.length > 1) out.push(block(lines.slice(1), i));
  return out;
});

export const KnowMoga = ({ exclude }) => {
  const { t, tn, lp } = useT();
  const { data = [] } = useApi("pages", "/pages");
  const list = data.filter((p) => p.slug !== exclude);
  if (!list.length) return null;
  return (
    <section className="px-5 pt-8 lg:px-8 lg:pt-10" data-testid="know-moga">
      <h2 className="font-display text-xl font-extrabold text-ink">{t("know_moga")}</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {list.map((p) => {
          const Icon = PAGE_ICONS[p.icon] || Landmark;
          const urgent = p.slug === "emergency";
          return (
            <Link key={p.slug} to={lp(`/about-moga/${p.slug}`)} data-testid={`know-moga-${p.slug}`}
              className={`group flex flex-col justify-between gap-4 rounded-2xl border p-4 transition-transform hover:-translate-y-0.5 ${urgent ? "border-red-200 bg-red-50" : "border-line bg-white"}`}>
              <Icon className={`h-7 w-7 ${urgent ? "text-red-600" : "text-saffron"}`} />
              <span className="flex items-end justify-between gap-2 font-display text-sm font-bold leading-tight text-ink md:text-base">{tn(p.title)}<ArrowRight className="h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5" /></span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default function MogaPage() {
  const { slug } = useParams();
  const { tn, lang } = useT();
  const { data, isLoading } = useApi(["page", slug], `/pages/${slug}`);
  const title = data ? tn(data.title) : "";
  return (
    <Layout narrow seo={data ? { title, description: (data.body.en || "").slice(0, 155) } : undefined}>
      {isLoading ? <Loading /> : data && (
        <>
          <PageTitle title={title} />
          <article className="px-5 pb-6 text-base" data-testid="moga-page-body"><Body text={data.body[lang] || data.body.en} /></article>
          <KnowMoga exclude={slug} />
        </>
      )}
    </Layout>
  );
}
